/**
 * Login: email + password. POST /api/auth/cookie/login (form-urlencoded).
 * После успеха useAuth.refresh() обновит контекст и router-guard проведёт
 * на /.
 *
 * Дополнительно — два mode: "register" (открытая регистрация → admin одобряет),
 * "forgot" (POST /api/auth/forgot-password — отправляет письмо).
 */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";

import { api, ApiError } from "@/api/client";
import {
  AUTH_EXPIRED_NOTICE_KEY,
  AUTH_RETURN_TO_KEY,
  safeAuthReturnTo,
  useAuth,
} from "@/auth/AuthContext";
import { Icon } from "@/components/prototype/Icon";
import { messengerSignup } from "@/api/orgs";
import "@/styles/brixchat.css";



const IS_CHAT_APP = (import.meta.env.VITE_APP_MODE as string | undefined) === "chat";
const IS_BRIXCHAT = typeof window !== "undefined" && /(^|\.)brixchat\.ru$|^chat\.brixcrm\.ru$/.test(window.location.hostname);
const _HOST = typeof window !== "undefined" ? window.location.hostname : "";
const IS_PRODUCTION_SUBDOMAIN = /^production\./i.test(_HOST);
const IS_MARKETING_SUBDOMAIN = /^marketing\./i.test(_HOST);
const IS_FINANCE_SUBDOMAIN = /^finance\./i.test(_HOST);
const IS_WAREHOUSE_SUBDOMAIN = /^warehouse\./i.test(_HOST);

// Какие роли разрешены в каждом отделе
const DEPT_ROLES: Record<string, Array<[string, string]>> = {
  admin: [["assistant", "Ассистент"], ["accountant", "Бухгалтер"]],
  sales: [["op_manager", "Менеджер ОП"], ["kc_manager", "Менеджер КЦ"], ["field_engineer", "Выездной инженер"]],
  production: [["builder", "Строитель"], ["brigade", "Бригадир"], ["engineer", "Инженер"], ["field_engineer", "Выездной инженер"]],
  supply: [["warehouse_keeper", "Кладовщик"]],
};

type Mode = "login" | "register" | "forgot";


/** Юзер вводит просто логин (например `igor`). Backend требует email-формат
 *  → добавляем @local.kb если нет @. Если уже email — оставляем как есть. */
function toEmail(login: string): string {
  const v = login.trim();
  return v.includes("@") ? v : `${v}@local.kb`;
}


export function LoginPage() {
  const { login: doLogin, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirect?: string };

  const [mode, setMode] = useState<Mode>("login");
  const [loginValue, setLoginValue] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [regDepartments, setRegDepartments] = useState<string[]>([]);
  const [regRole, setRegRole] = useState<string>("");
  const [website, setWebsite] = useState("");   // ловушка для ботов (регистрация мессенджера)
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: "error" | "success"; text: string; key: number } | null>(null);
  const [shake, setShake] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const formRef = useRef<HTMLFormElement>(null);
  // Мессенджер (03.10.2026): регистрация открыта всем, без одобрения админом и без отделов.
  const CHAT_SIGNUP = IS_CHAT_APP && mode === "register";

  // Auto-dismiss toast через 5s
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 5000);
    return () => window.clearTimeout(t);
  }, [toast]);

  // После автоматического выхода объясняем, почему открылся login. Ключ
  // одноразовый, чтобы сообщение не повторялось после обычного обновления.
  useEffect(() => {
    try {
      if (sessionStorage.getItem(AUTH_EXPIRED_NOTICE_KEY) === "1") {
        sessionStorage.removeItem(AUTH_EXPIRED_NOTICE_KEY);
        showToast("error", "Сессия истекла. Войдите снова — затем вернём вас обратно.");
      }
    } catch { /* sessionStorage может быть недоступен */ }
  }, []);

  // Сбросить shake-анимацию после её окончания
  useEffect(() => {
    if (!shake) return;
    const t = window.setTimeout(() => setShake(false), 350);
    return () => window.clearTimeout(t);
  }, [shake]);

  function showToast(kind: "error" | "success", text: string) {
    setToast({ kind, text, key: Date.now() });
  }

  function finishLogin() {
    let storedReturnTo: string | null = null;
    try { storedReturnTo = sessionStorage.getItem(AUTH_RETURN_TO_KEY); } catch { /* ignore */ }
    const target = safeAuthReturnTo(search?.redirect)
      ?? safeAuthReturnTo(storedReturnTo)
      ?? "/";
    try {
      sessionStorage.removeItem(AUTH_RETURN_TO_KEY);
      sessionStorage.removeItem(AUTH_EXPIRED_NOTICE_KEY);
    } catch { /* ignore */ }
    void navigate({ to: target as "/" });
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      const emailForBackend = toEmail(loginValue);
      if (mode === "login") {
        if (mfaChallenge) {
          await verifyMfa(mfaChallenge, mfaCode.trim());
          setMfaChallenge(null);
          setMfaCode("");
          finishLogin();
        } else {
          const result = await doLogin(emailForBackend, password);
          if (result.mfaRequired && result.challengeToken) {
            setMfaChallenge(result.challengeToken);
            setMfaCode("");
            setPassword("");
            return;
          }
          finishLogin();
        }
      } else if (CHAT_SIGNUP) {
        const email = loginValue.trim().toLowerCase();
        await messengerSignup({ display_name: displayName.trim(), email, password, website });
        await doLogin(email, password);
        finishLogin();
      } else if (mode === "register") {
        await api("/api/auth/register", {
          method: "POST",
          body: JSON.stringify({
            email: emailForBackend, password, display_name: displayName, role: regRole || "op_manager", departments: regDepartments,
          }),
        });
        setSuccess("Заявка отправлена. Администратор должен одобрить доступ. После этого вы сможете войти.");
        setMode("login");
        setPassword("");
      } else if (mode === "forgot") {
        await api("/api/auth/forgot-password", {
          method: "POST",
          body: JSON.stringify({ email: emailForBackend }),
        });
        setSuccess("Если такой логин существует и у него привязан Telegram-бот, ссылка сброса пришла в Telegram. Не привязан? Обратитесь к администратору.");
        setMode("login");
      }
    } catch (e) {
      let msg: string;
      if (e instanceof ApiError) {
        if (mfaChallenge && (e.status === 400 || e.status === 401)) {
          msg = "Неверный или просроченный одноразовый код";
        } else if (mode === "login" && (e.status === 400 || e.status === 401)) {
          msg = "Неверный логин или пароль";
        } else if (CHAT_SIGNUP) {
          msg = e.message || `Ошибка ${e.status}`;
        } else if (mode === "register" && e.status === 400) {
          msg = "Этот логин уже занят или пароль слишком короткий (≥ 6 символов)";
        } else if (mode === "forgot") {
          // forgot — это не ошибка для пользователя (нельзя light up невалидные emails)
          setSuccess("Если такой логин существует — ссылка сброса отправлена в Telegram.");
          setMode("login");
          return;
        } else {
          msg = `Ошибка ${e.status}`;
        }
      } else {
        msg = "Сервер недоступен. Проверьте интернет-соединение";
      }
      setError(msg);
      showToast("error", msg);
      if (mode === "login") setShake(true);
    } finally {
      setSubmitting(false);
    }
  }

  const heading = mfaChallenge ? "Подтверждение входа"
                : mode === "register" ? "Регистрация"
                : mode === "forgot"   ? "Восстановление пароля"
                : null;

  return (
    <div className={IS_BRIXCHAT ? "login-shell bxc-login" : "login-shell"}>
      {toast && (
        <div className="toast-stack">
          <div key={toast.key} className={`toast toast-${toast.kind}`} role="alert">
            <div className="toast-body">{toast.text}</div>
            <button type="button" className="toast-close" onClick={() => setToast(null)} aria-label="Закрыть">×</button>
          </div>
        </div>
      )}
      {/* BrixChat — вход как у brixcrm (04.10.2026): фон, знак и слоган слева; форма та же */}
      {IS_BRIXCHAT && <div className="bxc-bg" aria-hidden="true"/>}
      {IS_BRIXCHAT && (
        <div className="bxc-cover">
          <div className="bxc-logo">
            <img src="/brand/brixchat/mark-256.png" alt="" width={56} height={56}/>
            <span>BrixChat</span>
          </div>
          <div className="bxc-pre">Рабочий мессенджер</div>
          <div className="bxc-big">для всей команды</div>
        </div>
      )}
      <div className="login-card">
        <div className="login-brand">
          {IS_BRIXCHAT ? (
            <img className="login-mark login-mark-brix" src="/brand/brixchat/mark-128.png" alt="" width={44} height={44}/>
          ) : IS_CHAT_APP ? (
            <div className="login-mark login-mark-chat" aria-hidden="true">
              <svg viewBox="0 0 32 32" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {/* Крыша */}
                <path d="M4 14 L16 4 L28 14 Z"/>
                {/* Шпиль/флажок */}
                <line x1="16" y1="4" x2="16" y2="2"/>
                {/* Стены */}
                <path d="M6 14 L6 26 L26 26 L26 14"/>
                {/* Опоры (колонны слева и справа) */}
                <line x1="6" y1="14" x2="6" y2="26"/>
                <line x1="26" y1="14" x2="26" y2="26"/>
                {/* Brick-кладка — горизонтальные ряды */}
                <line x1="6" y1="18" x2="26" y2="18"/>
                <line x1="6" y1="22" x2="26" y2="22"/>
                {/* Шахматный шов кирпичей */}
                <line x1="13" y1="14" x2="13" y2="18"/>
                <line x1="20" y1="14" x2="20" y2="18"/>
                <line x1="10" y1="18" x2="10" y2="22"/>
                <line x1="16" y1="18" x2="16" y2="22"/>
                <line x1="22" y1="18" x2="22" y2="22"/>
                <line x1="13" y1="22" x2="13" y2="26"/>
                <line x1="20" y1="22" x2="20" y2="26"/>
              </svg>
            </div>
          ) : (
            <div className="login-mark">К</div>
          )}
          <div>
            <div className="login-title">{IS_BRIXCHAT ? "BrixChat" : IS_CHAT_APP ? "Мессенджер" : "Кирпичные беседки"}</div>
            <div className="login-sub">{
              IS_BRIXCHAT ? "Рабочий мессенджер"
              : IS_CHAT_APP ? "Кирпичные беседки"
              : IS_PRODUCTION_SUBDOMAIN ? "Производство"
              : IS_MARKETING_SUBDOMAIN ? "Дашборд отдела маркетинга"
              : IS_FINANCE_SUBDOMAIN ? "Финансы"
              : IS_WAREHOUSE_SUBDOMAIN ? "Склад"
              : "Дашборд отдела продаж"
            }</div>
          </div>
        </div>

        {IS_BRIXCHAT && <h2 className="bxc-title">{heading ?? "Вход в BrixChat"}</h2>}
        {heading && (
          <div className="login-heading">{heading}</div>
        )}
        {success && <div className="alert alert-green" style={{ marginBottom: 12 }}><div className="alert-body">{success}</div></div>}

        <form ref={formRef} onSubmit={onSubmit} className={`login-form${shake ? " is-shake" : ""}`}>
          {mode === "register" && (
            <label className="login-label">
              <span>Имя</span>
              <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required disabled={submitting} placeholder="Иван Иванов"/>
            </label>
          )}

          {!mfaChallenge && <label className="login-label">
            <span>{CHAT_SIGNUP ? "Электронная почта" : IS_CHAT_APP ? "Логин или почта" : "Логин"}</span>
            <input
              type={CHAT_SIGNUP ? "email" : "text"}
              autoComplete={CHAT_SIGNUP ? "email" : "username"}
              value={loginValue}
              onChange={(e) => {
                // На регистрации чистим пробелы и приводим к нижнему регистру —
                // логин конвертируется в email (login@kb-dashboard.local), а в email
                // не разрешены пробелы / спец-символы
                const v = mode === "register"
                  ? e.target.value.replace(/\s/g, "").toLowerCase()
                  : e.target.value;
                setLoginValue(v);
              }}
              required
              disabled={submitting}
              placeholder={CHAT_SIGNUP ? "name@company.ru" : mode === "register" ? "ivanov" : ""}
              pattern={mode === "register" && !IS_CHAT_APP ? "[a-zA-Z0-9._-]+" : undefined}
              title={mode === "register" && !IS_CHAT_APP ? "Только латиница, цифры, точка, дефис и подчёркивание" : undefined}
            />
          </label>}

          {mode !== "forgot" && !mfaChallenge && (
            <label className="login-label">
              <span>Пароль{CHAT_SIGNUP ? " (не короче 10 символов)" : mode === "register" ? " (≥ 6 символов)" : ""}</span>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete={mode === "register" ? "new-password" : "current-password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={CHAT_SIGNUP ? 10 : mode === "register" ? 6 : undefined}
                  disabled={submitting}
                  style={{ width: "100%", paddingRight: 38 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  title={showPassword ? "Скрыть пароль" : "Показать пароль"}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "transparent",
                    border: "none",
                    color: "var(--mute)",
                    cursor: "pointer",
                    padding: 4,
                    display: "grid",
                    placeItems: "center",
                  }}
                  tabIndex={-1}
                >
                  <Icon name={showPassword ? "eye_off" : "eye"} size={16}/>
                </button>
              </div>
            </label>
          )}

          {mfaChallenge && (
            <label className="login-label">
              <span>Код из приложения-аутентификатора</span>
              <input
                type="text"
                inputMode="text"
                autoComplete="one-time-code"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.toUpperCase())}
                required
                minLength={6}
                maxLength={32}
                disabled={submitting}
                autoFocus
                placeholder="123456 или резервный код"
                style={{ letterSpacing: 2, textAlign: "center" }}
              />
              <span style={{ fontSize: 12, color: "var(--mute)", lineHeight: 1.4 }}>
                Введите 6-значный код или один из сохранённых резервных кодов.
              </span>
            </label>
          )}

          {CHAT_SIGNUP && (
            <>
              <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
                value={website} onChange={(e) => setWebsite(e.target.value)}
                style={{ position: "absolute", left: -10000, width: 1, height: 1, opacity: 0 }}/>
              <div style={{ fontSize: 12, color: "var(--mute)", marginTop: -4, lineHeight: 1.45 }}>
                После входа укажите свою организацию — коллеги увидят вас в мессенджере,
                когда её владелец подтвердит заявку.
              </div>
            </>
          )}
          {mode === "register" && !IS_CHAT_APP && (
            <>
              <div className="login-label">
                <span>Отделы (можно несколько)</span>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                  {[
                    ["admin", "Административный"],
                    ["sales", "Отдел продаж"],
                    ["production", "Производственный"],
                    ["supply", "Отдел снабжения"],
                  ].map(([id, label]) => {
                    const checked = regDepartments.includes(id);
                    return (
                      <label
                        key={id}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 6,
                          padding: "6px 12px", borderRadius: 16,
                          background: checked ? "var(--login-chip-bg-on, #e8f5e9)" : "var(--login-chip-bg-off, #f4f4f5)",
                          border: `1px solid ${checked ? "var(--login-chip-border-on, #4fae4e)" : "var(--line, #ddd)"}`,
                          color: checked ? "var(--login-chip-fg-on, #2e7d32)" : "var(--ink-2, #444)",
                          fontSize: 13, cursor: "pointer", userSelect: "none",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setRegDepartments(prev => {
                              const next = checked ? prev.filter(x => x !== id) : [...prev, id];
                              // если выбранная роль больше не подходит ни к одному отделу — сбросить
                              const allowed = new Set(next.flatMap(d => (DEPT_ROLES[d] || []).map(r => r[0])));
                              if (regRole && !allowed.has(regRole)) setRegRole("");
                              return next;
                            });
                          }}
                          disabled={submitting}
                          style={{ accentColor: "var(--login-accent, #4fae4e)" }}
                        />
                        {label}
                      </label>
                    );
                  })}
                </div>
              </div>
              {regDepartments.length > 0 && (() => {
                // Объединяем roles из всех выбранных отделов (deduplicated)
                const seen = new Set<string>();
                const opts: Array<[string, string]> = [];
                for (const d of regDepartments) {
                  for (const [v, l] of (DEPT_ROLES[d] || [])) {
                    if (!seen.has(v)) { seen.add(v); opts.push([v, l]); }
                  }
                }
                return (
                  <label className="login-label">
                    <span>Роль</span>
                    <select
                      value={regRole}
                      onChange={(e) => setRegRole(e.target.value)}
                      required
                      disabled={submitting}
                      style={{ width: "100%" }}
                    >
                      <option value="">— выберите роль —</option>
                      {opts.map(([val, label]) => (
                        <option key={val} value={val}>{label}</option>
                      ))}
                    </select>
                  </label>
                );
              })()}
              <div style={{ fontSize: 12, color: "var(--mute)", marginTop: -4 }}>
                Заявка отправится администратору на одобрение. После этого вы
                сможете войти. Привяжите Telegram-бота в Настройках, чтобы
                восстанавливать пароль через бота.
              </div>
            </>
          )}
          {mode === "forgot" && (
            <div style={{ fontSize: 12, color: "var(--mute)", marginTop: -4 }}>
              Сброс пароля идёт через привязанный Telegram-бот. Если не привязан — попросите
              администратора сбросить вручную.
            </div>
          )}

          {error && <div className="alert alert-red"><div className="alert-body">{error}</div></div>}

          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? "…" :
             mfaChallenge ? "Подтвердить" :
             mode === "register" ? "Зарегистрироваться" :
             mode === "forgot"   ? "Отправить ссылку" :
             "Войти"}
          </button>
        </form>

        {/* Переключатели режимов */}
        <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", fontSize: 12 }}>
          {mode === "login" && !mfaChallenge && (
            <>
              <a onClick={() => { setMode("forgot"); setError(null); setSuccess(null); }} className="login-link">
                Забыли пароль?
              </a>
              <a onClick={() => { setMode("register"); setError(null); setSuccess(null); }} className="login-link">
                Регистрация →
              </a>
            </>
          )}
          {mfaChallenge && (
            <a onClick={() => { setMfaChallenge(null); setMfaCode(""); setError(null); }} className="login-link">
              ← Назад к вводу пароля
            </a>
          )}
          {mode !== "login" && (
            <a onClick={() => { setMode("login"); setError(null); setSuccess(null); }} className="login-link">
              ← Назад к входу
            </a>
          )}
        </div>
      </div>
      {IS_BRIXCHAT && <footer className="bxc-foot">© {new Date().getFullYear()} brixchat.ru</footer>}
    </div>
  );
}
